import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Divider,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  NewReleases as NovidadesIcon,
  Lightbulb as SugestoesIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { novidadesApi, Novidade } from '../utils/novidadesApi';
import { sugestoesApi, Sugestao, SugestaoQueryRQ } from '../utils/sugestoesApi';
import toast from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`novidades-tabpanel-${index}`}
      aria-labelledby={`novidades-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const NovidadesViewPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [novidades, setNovidades] = useState<Novidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para Sugestões
  const [sugestoes, setSugestoes] = useState<Sugestao[]>([]);
  const [loadingSugestoes, setLoadingSugestoes] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ texto: '' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<SugestaoQueryRQ>({
    orderBy: 'votosPositivos',
    ascending: false,
  });

  useEffect(() => {
    if (tabValue === 0) {
      loadNovidades();
    } else if (tabValue === 1) {
      loadSugestoes();
    }
  }, [tabValue, filters]);

  const loadNovidades = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await novidadesApi.getActive();
      setNovidades(data);
    } catch (error: any) {
      setError('Erro ao carregar novidades: ' + (error.message || 'Erro desconhecido'));
      toast.error('Erro ao carregar novidades');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const loadSugestoes = async () => {
    try {
      setLoadingSugestoes(true);
      setError(null);
      const data = await sugestoesApi.query(filters);
      setSugestoes(data);
    } catch (error: any) {
      setError('Erro ao carregar sugestões: ' + (error.message || 'Erro desconhecido'));
      toast.error('Erro ao carregar sugestões');
    } finally {
      setLoadingSugestoes(false);
    }
  };

  const handleCreateSugestao = async () => {
    if (!formData.texto.trim()) {
      toast.error('Por favor, digite uma sugestão');
      return;
    }

    try {
      await sugestoesApi.create({ texto: formData.texto });
      toast.success('Sugestão criada com sucesso!');
      setDialogOpen(false);
      setFormData({ texto: '' });
      await loadSugestoes();
    } catch (error: any) {
      toast.error('Erro ao criar sugestão: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleVote = async (sugestaoId: string, isPositivo: boolean) => {
    try {
      await sugestoesApi.vote({ sugestaoId, isPositivo });
      await loadSugestoes();
    } catch (error: any) {
      toast.error('Erro ao votar: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
          color="inherit"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flex: 1 }}>
          Novidades e Sugestões
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            icon={<NovidadesIcon />}
            iconPosition="start"
            label="Novidades"
            id="novidades-tab-0"
            aria-controls="novidades-tabpanel-0"
          />
          <Tab
            icon={<SugestoesIcon />}
            iconPosition="start"
            label="Sugestões"
            id="novidades-tab-1"
            aria-controls="novidades-tabpanel-1"
          />
        </Tabs>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Novidades Tab */}
      <TabPanel value={tabValue} index={0}>
        {novidades.length === 0 ? (
          <Alert severity="info">
            Nenhuma novidade disponível no momento.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {novidades.map((novidade) => (
              <Card
                key={novidade.id}
                sx={{
                  boxShadow: novidade.destaque ? 4 : 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {novidade.titulo}
                      </Typography>
                      {novidade.destaque && (
                        <Chip
                          icon={<StarIcon />}
                          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                          label="Destaque"
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  }
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      {novidade.dataLancamento && (
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(novidade.dataLancamento)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  {novidade.descricao && (
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ mb: 2, fontStyle: 'italic' }}
                    >
                      {novidade.descricao}
                    </Typography>
                  )}
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.8,
                      color: 'text.primary',
                    }}
                  >
                    {novidade.conteudo}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Sugestões Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          >
            Nova Sugestão
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            Filtros
          </Button>
        </Box>

        {/* Filtros */}
        {filtersOpen && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <TextField
                label="Data de Criação (Início)"
                type="date"
                fullWidth
                value={filters.dataCriacaoInicio || ''}
                onChange={(e) => setFilters({ ...filters, dataCriacaoInicio: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Data de Criação (Fim)"
                type="date"
                fullWidth
                value={filters.dataCriacaoFim || ''}
                onChange={(e) => setFilters({ ...filters, dataCriacaoFim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Data de Atualização (Início)"
                type="date"
                fullWidth
                value={filters.dataAtualizacaoInicio || ''}
                onChange={(e) => setFilters({ ...filters, dataAtualizacaoInicio: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Data de Atualização (Fim)"
                type="date"
                fullWidth
                value={filters.dataAtualizacaoFim || ''}
                onChange={(e) => setFilters({ ...filters, dataAtualizacaoFim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Votos Positivos (Mín)"
                type="number"
                fullWidth
                value={filters.votosPositivosMin || ''}
                onChange={(e) => setFilters({ ...filters, votosPositivosMin: e.target.value ? parseInt(e.target.value) : undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Votos Positivos (Máx)"
                type="number"
                fullWidth
                value={filters.votosPositivosMax || ''}
                onChange={(e) => setFilters({ ...filters, votosPositivosMax: e.target.value ? parseInt(e.target.value) : undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Votos Negativos (Mín)"
                type="number"
                fullWidth
                value={filters.votosNegativosMin || ''}
                onChange={(e) => setFilters({ ...filters, votosNegativosMin: e.target.value ? parseInt(e.target.value) : undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Votos Negativos (Máx)"
                type="number"
                fullWidth
                value={filters.votosNegativosMax || ''}
                onChange={(e) => setFilters({ ...filters, votosNegativosMax: e.target.value ? parseInt(e.target.value) : undefined })}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.orderBy || 'votosPositivos'}
                  onChange={(e) => setFilters({ ...filters, orderBy: e.target.value })}
                  label="Ordenar por"
                >
                  <MenuItem value="votosPositivos">Votos Positivos</MenuItem>
                  <MenuItem value="votosNegativos">Votos Negativos</MenuItem>
                  <MenuItem value="dataCriacao">Data de Criação</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Ordem</InputLabel>
                <Select
                  value={filters.ascending ? 'asc' : 'desc'}
                  onChange={(e) => setFilters({ ...filters, ascending: e.target.value === 'asc' })}
                  label="Ordem"
                >
                  <MenuItem value="desc">Descendente</MenuItem>
                  <MenuItem value="asc">Ascendente</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      orderBy: 'votosPositivos',
                      ascending: false,
                    });
                  }}
                >
                  Limpar Filtros
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {loadingSugestoes ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : sugestoes.length === 0 ? (
          <Alert severity="info">
            Nenhuma sugestão disponível no momento.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {sugestoes.map((sugestao) => (
              <Card
                key={sugestao.id}
                sx={{
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.8,
                      color: 'text.primary',
                      mb: 2,
                    }}
                  >
                    {sugestao.texto}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton
                        color={sugestao.meuVoto === true ? 'primary' : 'default'}
                        onClick={() => handleVote(sugestao.id, true)}
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <ThumbUpIcon />
                      </IconButton>
                      <Typography variant="body2" color="text.secondary">
                        {sugestao.votosPositivos}
                      </Typography>
                      <IconButton
                        color={sugestao.meuVoto === false ? 'error' : 'default'}
                        onClick={() => handleVote(sugestao.id, false)}
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <ThumbDownIcon />
                      </IconButton>
                      <Typography variant="body2" color="text.secondary">
                        {sugestao.votosNegativos}
                      </Typography>
                    </Box>
                    {sugestao.creationTime && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(sugestao.creationTime)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Dialog para criar sugestão */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nova Sugestão</DialogTitle>
        <DialogContent>
          <TextField
            label="Sua sugestão"
            fullWidth
            multiline
            rows={6}
            value={formData.texto}
            onChange={(e) => setFormData({ texto: e.target.value })}
            variant="outlined"
            placeholder="Digite sua sugestão aqui..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleCreateSugestao}
            variant="contained"
            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          >
            Publicar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NovidadesViewPage;

